import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMe } from '@/api/hooks/useMe';
import {
  useInboundMailbox,
  useRemintInboundMailbox,
  useRevokeInboundMailbox,
} from '@/api/hooks/useInboundMailbox';
import { scopeHref } from '@/app/shell/scope';
import { ConfirmationRows } from './receiptForwardingControls';
import { TakeoutUploadSection } from './TakeoutUploadSection';

const HEALTH_COPY: Record<string, string> = {
  waiting: 'No receipt mail received.',
  ok: 'Receipt mail is arriving.',
  unparsed: 'Mail received; not a recognized receipt yet.',
  verification: 'A forwarding confirmation was sent to this address.',
  revoked: 'Receipt email is off. Create a new address to start again.',
  error: 'Receipt email is unavailable.',
};

function formatAcceptedAt(iso: string, timezone: string | undefined): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime()) || !timezone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return null;
  }
}

function healthCopy(
  health: string,
  lastAcceptedAt: string | null,
  timezone?: string,
  verificationHost?: string | null,
): string {
  if (health === 'verification') {
    if (verificationHost === 'gmail') return 'Gmail sent a confirmation to this address.';
    if (verificationHost === 'outlook') return 'Outlook sent a confirmation to this address.';
  }
  if (health === 'ok' && lastAcceptedAt) {
    const formatted = formatAcceptedAt(lastAcceptedAt, timezone);
    return formatted ? `Last receipt mail accepted ${formatted}.` : HEALTH_COPY.ok;
  }
  return HEALTH_COPY[health] ?? HEALTH_COPY.waiting;
}

export function ReceiptEmailCard() {
  const { search } = useLocation();
  const meQuery = useMe();
  const mailboxQuery = useInboundMailbox();
  const revoke = useRevokeInboundMailbox();
  const remint = useRemintInboundMailbox();
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  const mailbox = mailboxQuery.data;
  const address = mailbox?.address?.toLowerCase() ?? null;
  const copied = mailbox?.status === 'active' && address !== null && copiedAddress === address;

  const copyAddress = async () => {
    if (!address) return;
    await navigator.clipboard.writeText(address);
    setCopiedAddress(address);
  };

  return (
    <>
    <section className="space-y-3 rounded-lg border border-border p-4 sm:p-6" aria-labelledby="receipt-email-heading">
      <div>
        <h2 id="receipt-email-heading" className="text-lg font-semibold">Receipt email</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Forward order confirmations, shipments, and receipts to this address. Bank transactions stay the money record.
        </p>
        <Link
          to={scopeHref('/settings/receipt-email', search)}
          className="ms-tap-44 mt-2 inline-flex min-h-11 items-center text-sm text-link underline"
        >
          Email forwarding instructions
        </Link>
      </div>

      {mailboxQuery.isPending && (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading receipt email…</p>
      )}
      {mailboxQuery.isError && (
        <div className="rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm" role="alert">
          <p>Receipt email could not be loaded.</p>
          <button type="button" onClick={() => mailboxQuery.refetch()} className="mt-3 min-h-11 rounded-md bg-primary px-4 text-primary-foreground">Retry</button>
        </div>
      )}

      {mailbox && mailbox.status === 'active' && address && (
        <>
          <div className="space-y-1.5">
            <label htmlFor="receipt-email-address" className="text-sm font-medium">Forwarding address</label>
            <div className="flex min-h-11 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <input
                id="receipt-email-address"
                readOnly
                value={address}
                onFocus={(event) => event.currentTarget.select()}
                className="min-h-11 min-w-0 flex-1 border-0 bg-transparent px-3 font-mono text-sm outline-none"
              />
              <button
                type="button"
                onClick={() => void copyAddress()}
                className="min-h-11 shrink-0 border-l border-input px-4 text-sm hover:bg-muted"
                aria-label={copied ? 'Copied' : 'Copy address'}
                aria-live="polite"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <p className="text-sm" data-testid="receipt-email-health">
            {healthCopy(mailbox.health, mailbox.last_accepted_at, meQuery.data?.timezone, mailbox.verification_host)}
          </p>
          <ConfirmationRows mailbox={mailbox} />
          <button
            type="button"
            onClick={() => revoke.mutate()}
            disabled={revoke.isPending}
            className="min-h-11 rounded-md border border-border px-4 text-sm"
          >
            {revoke.isPending ? 'Turning off…' : 'Turn off receipt email'}
          </button>
        </>
      )}

      {mailbox && mailbox.status === 'revoked' && (
        <>
          <p className="text-sm" data-testid="receipt-email-health">{HEALTH_COPY.revoked}</p>
          <button
            type="button"
            onClick={() => remint.mutate()}
            disabled={remint.isPending}
            className="min-h-11 rounded-md bg-primary px-4 font-semibold text-primary-foreground"
          >
            {remint.isPending ? 'Creating…' : 'Create a new address'}
          </button>
        </>
      )}
    </section>
    <TakeoutUploadSection />
    </>
  );
}
